/**
 * One diligence project: the Financial tabs, the report, versions and the source.
 *
 * Every figure here is the server's - the checks run on the pinned statements in
 * app/diligence - so this screen only shows them and records a person's decisions. Every
 * finding carries its source and its arithmetic; a proposal from a model reaches no total
 * until someone accepts it.
 */

import { useEffect, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { formatMoney } from '../../../lib/currency';
import {
  useDiligenceStore,
  type Finding,
  type Severity,
  type Status,
} from '../../../store/diligence-projects';
import DisciplineSwitch from '../DisciplineSwitch';
import type { Discipline, DisciplineId } from '../disciplines';
import { Button } from '../ui/Button';
import { Notice } from '../ui/ProjectsPage';
import { WorkspaceShell, type ShellGroup } from '../ui/WorkspaceShell';

export const DILIGENCE_GROUPS: ShellGroup[] = [
  {
    id: 'dd-financial',
    label: 'Financial',
    tabs: [
      { id: 'dd-overview', label: 'Overview' },
      { id: 'dd-quality', label: 'Quality of earnings' },
      { id: 'dd-net-debt', label: 'Net debt' },
      { id: 'dd-working-capital', label: 'Working capital' },
      { id: 'dd-cash', label: 'Cash conversion' },
      { id: 'dd-red-flags', label: 'Red flags' },
    ],
  },
  {
    id: 'dd-corporate',
    label: 'Corporate',
    tabs: [{ id: 'dd-corporate', label: 'Register' }],
  },
  {
    id: 'dd-documents',
    label: 'Documents',
    tabs: [
      { id: 'dd-signals', label: 'Filing' },
      { id: 'dd-related-parties', label: 'Related parties' },
      { id: 'dd-data-room', label: 'Data room' },
    ],
  },
  {
    id: 'dd-outputs',
    label: 'Outputs',
    tabs: [
      { id: 'dd-report', label: 'Report' },
      { id: 'dd-requests', label: 'Request list' },
    ],
  },
  {
    id: 'dd-admin',
    label: '',
    tabs: [
      { id: 'dd-versions', label: 'Versions' },
      { id: 'dd-source', label: 'Source' },
    ],
  },
];

const SEVERITY_TONE: Record<Severity, string> = {
  high: 'bg-red-50 text-red-800 ring-red-200',
  medium: 'bg-amber-50 text-amber-900 ring-amber-200',
  low: 'bg-gray-50 text-gray-700 ring-gray-200',
  info: 'bg-sky-50 text-sky-800 ring-sky-200',
};

function Section({ title, children, note }: { title: string; children: ReactNode; note?: ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      {note && <p className="mt-1 text-xs text-gray-600">{note}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function pct(value: number | null): string {
  return value === null || value === undefined ? '-' : `${(value * 100).toFixed(0)}%`;
}

function days(value: number | null): string {
  return value === null || value === undefined ? '-' : value.toFixed(0);
}

export function FindingRow({
  finding,
  currency,
  onDecide,
}: {
  finding: Finding;
  currency: string;
  onDecide: (status: Status) => void;
}) {
  return (
    <li className="py-3" data-finding={finding.id}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={clsx('rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ring-1 ring-inset', SEVERITY_TONE[finding.severity])}>
              {finding.severity}
            </span>
            <span className="text-sm font-medium text-gray-900">{finding.title}</span>
            {finding.unvouched && (
              <span className="text-xs text-amber-700">rests on a figure the import could not vouch for</span>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-600">{finding.computation}</p>
          <p className="mt-0.5 text-xs text-gray-400">Source: {finding.source}</p>
          {!!finding.citations?.length && (
            <details className="mt-1 text-xs text-gray-500">
              <summary className="cursor-pointer">{finding.citations.length} figures cited</summary>
              <ul className="mt-1 space-y-0.5 pl-3" data-citations={finding.id}>
                {finding.citations.map((c, i) => (
                  <li key={i}>
                    {c.document}
                    {c.page !== null && `, page ${c.page}`}
                    {c.row && `, ${c.row}`}
                    {c.period && ` (${c.period})`}: <span className="font-mono tabular-nums">{formatMoney(c.figure, currency)}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {finding.amount !== null && (
            <span className="font-mono text-sm tabular-nums text-gray-800">{formatMoney(finding.amount, currency)}</span>
          )}
          <span className="flex gap-1">
            {(['accepted', 'rejected'] as Status[]).map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => onDecide(finding.status === status ? 'open' : status)}
                aria-pressed={finding.status === status}
                className={clsx(
                  'rounded px-2 py-0.5 text-xs ring-1 ring-inset',
                  finding.status === status ? 'bg-gray-900 text-white ring-gray-900' : 'text-gray-600 ring-gray-200 hover:bg-gray-50',
                )}
              >
                {status === 'accepted' ? 'Accept' : 'Reject'}
              </button>
            ))}
          </span>
        </div>
      </div>
    </li>
  );
}

function Findings({ findings, currency }: { findings: Finding[]; currency: string }) {
  const decide = useDiligenceStore((s) => s.decide);
  if (!findings.length) return <p className="text-sm text-gray-500">Nothing found.</p>;
  return (
    <ul className="divide-y divide-gray-100">
      {findings.map((f) => (
        <FindingRow key={f.id} finding={f} currency={currency} onDecide={(status) => void decide(f.id, status)} />
      ))}
    </ul>
  );
}

function NotAssessed({ lines }: { lines?: string[] }) {
  if (!lines?.length) return null;
  return (
    <Section title="Not assessed, and why">
      <ul className="list-disc space-y-1 pl-5 text-xs text-gray-600">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </Section>
  );
}

function AdjustmentForm({ years }: { years: number[] }) {
  const addAdjustment = useDiligenceStore((s) => s.addAdjustment);
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [year, setYear] = useState(years[years.length - 1] ?? 0);
  const [category, setCategory] = useState('non-recurring');
  return (
    <form
      className="mt-3 flex flex-wrap gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        const value = Number(amount.replace(/,/g, ''));
        if (label.trim() && !Number.isNaN(value)) {
          void addAdjustment({ label: label.trim(), year, amount: value, category });
          setLabel('');
          setAmount('');
        }
      }}
    >
      <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Adjustment" aria-label="Adjustment"
        className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm" />
      <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount (+ adds back)" aria-label="Amount"
        className="w-44 rounded-md border border-gray-300 px-2 py-1 text-right text-sm tabular-nums" />
      <select value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Year" className="rounded-md border border-gray-300 px-2 py-1 text-sm">
        {years.map((y) => <option key={y} value={y}>FY{y}</option>)}
      </select>
      <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category" className="rounded-md border border-gray-300 px-2 py-1 text-sm">
        {['one-off', 'owner cost', 'non-recurring', 'accounting policy', 'pro forma'].map((c) => <option key={c}>{c}</option>)}
      </select>
      <Button type="submit" size="sm" variant="secondary">Add</Button>
    </form>
  );
}

function SendToValuation() {
  const valuations = useDiligenceStore((s) => s.valuations);
  const listValuations = useDiligenceStore((s) => s.listValuations);
  const sendToValuation = useDiligenceStore((s) => s.sendToValuation);
  const project = useDiligenceStore((s) => s.project)!;
  const [target, setTarget] = useState('');
  useEffect(() => {
    void listValuations();
  }, [listValuations]);
  const sent = project.sentTo.length;
  return (
    <div className="mt-6 border-t border-gray-100 pt-4">
      <h3 className="text-sm font-semibold text-gray-900">Send to a valuation</h3>
      <p className="mt-1 text-xs text-gray-600">
        The accepted adjustments and the debt-like items this net debt includes go to the valuation as a separate layer
        it can remove - its own figures never change.
        {sent > 0 && ` Sent to ${sent} valuation${sent === 1 ? '' : 's'} so far.`}
      </p>
      <div className="mt-2 flex gap-2">
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          aria-label="Valuation"
          className="rounded-md border border-gray-300 px-2 py-1 text-sm"
        >
          <option value="">Choose a valuation</option>
          {valuations.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
        <Button size="sm" variant="secondary" disabled={!target} onClick={() => void sendToValuation(target)}>
          Send
        </Button>
      </div>
    </div>
  );
}

function CorporateTab() {
  const project = useDiligenceStore((s) => s.project)!;
  const readRegister = useDiligenceStore((s) => s.readRegister);
  const isSaving = useDiligenceStore((s) => s.isSaving);
  const [number, setNumber] = useState(project.source.companyNumber ?? '');
  const reg = project.companyRegister;
  const findings = project.assessment.overview.findings.filter((f) => f.tab === 'corporate');
  const currency = project.statements.currency || 'GBP';
  return (
    <div className="space-y-4">
      <Section
        title="The public register"
        note="From Companies House, read by code: charges, officers and control, the filing history. Public register data only, kept with the date it was read."
      >
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (number.trim()) void readRegister(number.trim());
          }}
        >
          <input
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="Company number"
            aria-label="Company number"
            className="w-48 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
          <Button type="submit" size="sm" variant="secondary" disabled={isSaving || !number.trim()}>
            {reg ? 'Read again' : 'Read the register'}
          </Button>
        </form>
        {reg && (
          <p className="mt-2 text-xs text-gray-500">
            {reg.name} · {reg.status} · incorporated {reg.incorporated} · read {reg.retrievedAt.slice(0, 10)}
          </p>
        )}
      </Section>
      {reg && (
        <>
          <Section title="Findings">
            <Findings findings={findings} currency={currency} />
          </Section>
          <Section title={`Charges (${reg.charges.length})`}>
            <ul className="divide-y divide-gray-100 text-sm">
              {reg.charges.map((c, i) => (
                <li key={i} className="py-1.5">
                  {c.holders.join(', ') || 'Holder not named'} · {c.classification} · created {c.createdOn} ·{' '}
                  <span className={c.status === 'outstanding' ? 'font-medium text-amber-800' : 'text-gray-500'}>{c.status}</span>
                </li>
              ))}
            </ul>
          </Section>
          <Section title="Officers and control">
            <ul className="divide-y divide-gray-100 text-sm">
              {reg.officers.map((o, i) => (
                <li key={i} className="py-1.5">
                  {o.name} · {o.role} · appointed {o.appointedOn}
                  {o.resignedOn ? ` · resigned ${o.resignedOn}` : ''}
                </li>
              ))}
              {reg.control.map((p, i) => (
                <li key={`c${i}`} className="py-1.5">
                  Control: {p.name} · {p.naturesOfControl.join(', ')}
                  {p.ceasedOn ? ` · ceased ${p.ceasedOn}` : ''}
                </li>
              ))}
            </ul>
          </Section>
          <Section title={`Filing history (${reg.filings.length})`}>
            <ul className="max-h-72 divide-y divide-gray-100 overflow-auto text-xs text-gray-700">
              {reg.filings.map((f, i) => (
                <li key={i} className="py-1">
                  {f.date} · {f.type} · {f.description}
                </li>
              ))}
            </ul>
          </Section>
        </>
      )}
      <NotAssessed lines={project.assessment.corporate.notAssessed} />
    </div>
  );
}

function RequestsTab() {
  const project = useDiligenceStore((s) => s.project)!;
  const generateRequests = useDiligenceStore((s) => s.generateRequests);
  const addRequest = useDiligenceStore((s) => s.addRequest);
  const editRequest = useDiligenceStore((s) => s.editRequest);
  const [text, setText] = useState('');
  return (
    <Section
      title="Request list"
      note="The questions and documents to ask the seller for, from the findings that could not be closed. Edit any; regenerating keeps your edits."
    >
      <Button size="sm" variant="secondary" onClick={() => void generateRequests()}>
        {project.requests.length ? 'Regenerate from the findings' : 'Generate from the findings'}
      </Button>
      <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm">
        {project.requests.map((r) => (
          <li key={r.id}>
            <div className="flex items-start justify-between gap-3">
              <span className={clsx(r.status === 'closed' && 'text-gray-400 line-through')}>{r.text}</span>
              <select
                value={r.status}
                onChange={(e) => void editRequest(r.id, { status: e.target.value })}
                aria-label="Request status"
                className="shrink-0 rounded border border-gray-300 px-1 py-0.5 text-xs"
              >
                <option value="open">open</option>
                <option value="sent">sent</option>
                <option value="closed">closed</option>
              </select>
            </div>
          </li>
        ))}
      </ol>
      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) {
            void addRequest(text.trim());
            setText('');
          }
        }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a request"
          aria-label="Add a request"
          className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
        />
        <Button type="submit" size="sm">
          Add
        </Button>
      </form>
    </Section>
  );
}

function SignalsTab() {
  const project = useDiligenceStore((s) => s.project)!;
  const tab = project.assessment.documents;
  const currency = project.statements.currency || 'GBP';
  const signalFindings = project.assessment.overview.findings.filter(
    (f) => f.tab === 'documents' && f.id.startsWith('documents:signal'),
  );
  return (
    <div className="space-y-4">
      <Section
        title="What the filing's text says"
        note="Going concern, the audit opinion, contingent liabilities, events after the year end and customer concentration - found by fixed patterns in the filing's text, each with the sentence it rests on and its page. The auditor's standard wording is not a signal."
      >
        <Findings findings={signalFindings} currency={currency} />
      </Section>
      <NotAssessed lines={(tab.notAssessed ?? []).filter((n) => n.startsWith('Going concern'))} />
    </div>
  );
}

/** Each row of the filing's related-party note, read by code, as a finding to check on its page. */
function RelatedPartiesTab() {
  const project = useDiligenceStore((s) => s.project)!;
  const tab = project.assessment['related-parties'];
  const currency = project.statements.currency || 'GBP';
  const note = tab?.note;
  return (
    <div className="space-y-4">
      <Section
        title="Related-party transactions"
        note="Each row of the filing's related-party note - counterparty, what it was, the amount and the year before - read by code from the filing's text, with the page and the words it was read from. A name is matched to the register's officers and persons with significant control where every word agrees. Directors' pay is not a row."
      >
        {tab?.validation && (
          <p className="mb-2 text-xs font-medium text-amber-700" data-validation="related-parties">
            {tab.validation}
          </p>
        )}
        {note?.none && (
          <p className="mb-2 text-xs text-gray-600">
            The note (page {note.page}) says there is nothing to disclose: &ldquo;{note.noneStatement}&rdquo;
          </p>
        )}
        <Findings findings={tab?.findings ?? []} currency={currency} />
      </Section>
      <NotAssessed lines={tab?.notAssessed} />
    </div>
  );
}

function UploadButton({ label, accept, onFile }: { label: string; accept: string; onFile: (file: File) => void }) {
  const isSaving = useDiligenceStore((s) => s.isSaving);
  return (
    <label
      className={clsx(
        'inline-flex cursor-pointer items-center rounded-md px-3 py-1.5 text-sm text-gray-800 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
        isSaving && 'pointer-events-none opacity-50',
      )}
    >
      {label}
      <input
        type="file"
        accept={accept}
        className="sr-only"
        aria-label={label}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = '';
        }}
      />
    </label>
  );
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthLabel(month: string): string {
  return `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]}-${month.slice(2, 4)}`;
}

/** Monthly packs as one series: each financial year's months against the filed year, LTM,
 * margin, seasonality and run-rate - with what the series has NOT been validated on. */
function MonthlySeriesSection() {
  const project = useDiligenceStore((s) => s.project)!;
  const upload = useDiligenceStore((s) => s.uploadManagementAccounts);
  const removePack = useDiligenceStore((s) => s.removeMonthlyPack);
  const [yearEnd, setYearEnd] = useState<number | null>(project.config.yearEndMonth ?? null);
  const currency = project.statements.currency || 'GBP';
  const series = project.assessment.documents.series;
  const findings = project.assessment.overview.findings.filter((f) => f.id.startsWith('documents:series'));
  const packs = project.monthlyPacks ?? [];
  const revenueLtm = series?.ltm.revenue ?? {};
  const lastLtm = Object.keys(revenueLtm).sort().pop();
  return (
    <Section
      title="Monthly series"
      note="A workbook or CSV whose columns are months. Packs add up: each financial year's twelve months are compared with the filed year (balances at the year's last month), within 1% and a pound. A year the packs cover in part is not compared."
    >
      <div className="flex flex-wrap items-center gap-2">
        <UploadButton label="Add a monthly pack" accept=".xlsx,.xlsm,.csv" onFile={(f) => void upload(f, yearEnd)} />
        <label className="flex items-center gap-1 text-xs text-gray-600">
          Year ends in
          <select
            className="rounded border border-gray-200 px-1 py-0.5 text-xs"
            value={yearEnd ?? ''}
            onChange={(e) => setYearEnd(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">
              {series?.yearEndMonth ? `${MONTH_NAMES[series.yearEndMonth - 1]} (${series.yearEndSource})` : 'not known'}
            </option>
            {MONTH_NAMES.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </label>
      </div>
      {series && (
        <p className="mt-2 text-xs font-medium text-amber-700" data-validation="monthly-series">
          {series.validation}
        </p>
      )}
      {packs.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-gray-500">
          {packs.map((p) => (
            <li key={p.name} className="flex items-center gap-2">
              {p.name} · uploaded {p.uploadedAt.slice(0, 10)} · {p.rows.length} rows, {p.rows.filter((r) => r.field).length} matched
              <button type="button" className="text-gray-400 underline" onClick={() => void removePack(p.name)}>
                remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {series && series.months.length > 0 && (
        <>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1">Financial year</th>
                  <th>Months</th>
                  <th>Line</th>
                  <th className="text-right">Months add up to</th>
                  <th className="text-right">Filed</th>
                  <th className="text-right">Difference</th>
                </tr>
              </thead>
              <tbody>
                {series.years.flatMap((y) =>
                  y.lines.map((line) => (
                    <tr key={`${y.year}-${line.field}`} className={clsx('border-t border-gray-100', line.differs && 'text-red-700')}>
                      <td className="py-1">FY{y.year}</td>
                      <td>{line.months ?? y.months} of 12</td>
                      <td>{line.field}</td>
                      <td className="text-right font-mono tabular-nums">
                        {line.management !== undefined ? formatMoney(line.management, currency) : '-'}
                      </td>
                      <td className="text-right font-mono tabular-nums">
                        {line.filed !== undefined ? formatMoney(line.filed, currency) : 'not filed'}
                      </td>
                      <td className="text-right font-mono tabular-nums">
                        {line.compared && line.difference !== undefined ? formatMoney(line.difference, currency) : 'not compared'}
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
          <dl className="mt-3 grid grid-cols-1 gap-2 text-xs text-gray-600 sm:grid-cols-3">
            <div>
              <dt className="text-gray-400">Revenue, last twelve months</dt>
              <dd>{lastLtm ? `${formatMoney(revenueLtm[lastLtm], currency)} to ${monthLabel(lastLtm)}` : 'needs twelve consecutive months'}</dd>
            </div>
            <div>
              <dt className="text-gray-400">Run-rate (last 3 months x 4)</dt>
              <dd>
                {series.runRate
                  ? `${formatMoney(series.runRate.annualised, currency)} against FY${series.runRate.filedYear} ${formatMoney(series.runRate.filed, currency)}`
                  : 'needs the last three months'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-400">Seasonality (share of the year&apos;s revenue)</dt>
              <dd>
                {Object.keys(series.seasonality).length
                  ? Object.entries(series.seasonality)
                      .map(([m, share]) => `${MONTH_NAMES[Number(m) - 1]} ${(share * 100).toFixed(1)}%`)
                      .join(' · ')
                  : 'needs a whole financial year'}
              </dd>
            </div>
          </dl>
          {Object.keys(series.grossMargin).length > 0 && (
            <p className="mt-2 text-xs text-gray-600">
              Gross margin by month:{' '}
              {Object.entries(series.grossMargin)
                .map(([m, margin]) => `${monthLabel(m)} ${(margin * 100).toFixed(1)}%`)
                .join(' · ')}
            </p>
          )}
          <div className="mt-3">
            <Findings findings={findings} currency={currency} />
          </div>
        </>
      )}
    </Section>
  );
}

/** Bank statements against the management accounts' cash and the filed cash. */
function ProofOfCashSection() {
  const project = useDiligenceStore((s) => s.project)!;
  const upload = useDiligenceStore((s) => s.uploadDocument);
  const remove = useDiligenceStore((s) => s.removeDocument);
  const [account, setAccount] = useState('Current account');
  const currency = project.statements.currency || 'GBP';
  const tab = project.assessment['proof-of-cash'];
  const statements = project.bankStatements ?? [];
  return (
    <Section
      title="Bank statements: proof of cash"
      note="A PDF or CSV statement per upload, named by its account (never its number). A statement is used only where its balance rolls from opening to closing; each month's closing balance is compared with the management accounts' cash, and the year end with the filed cash, to the pound. Account numbers, sort codes and card numbers are taken out of every description before anything is kept."
    >
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="rounded border border-gray-200 px-2 py-1 text-xs"
          value={account}
          onChange={(e) => setAccount(e.target.value)}
          aria-label="Account name"
        />
        <UploadButton label="Add a statement" accept=".pdf,.csv" onFile={(f) => void upload('bank-statements', f, account)} />
      </div>
      {tab && (
        <p className="mt-2 text-xs font-medium text-amber-700" data-validation="proof-of-cash">
          {tab.validation}
        </p>
      )}
      {statements.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-gray-500">
          {statements.map((s) => (
            <li key={s.name} className="flex items-center gap-2">
              {s.account} · {s.bank || 'bank not named'} · {s.name} · {s.start ?? '?'} to {s.end ?? '?'} ·{' '}
              <span className={s.proved ? 'text-emerald-700' : 'text-red-700'}>{s.proved ? 'rolls' : 'does not roll'}</span>
              <button type="button" className="text-gray-400 underline" onClick={() => void remove('bank-statements', s.name)}>
                remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {tab && tab.months.length > 0 && (
        <p className="mt-2 text-xs text-gray-600">
          Reconciled months: {tab.months.length} ({tab.months[0].month} to {tab.months[tab.months.length - 1].month}); closing{' '}
          {formatMoney(tab.months[tab.months.length - 1].closing, currency)}.
        </p>
      )}
      <div className="mt-3">
        <Findings findings={tab?.findings ?? []} currency={currency} />
      </div>
    </Section>
  );
}

/** Revenue per VAT returns, per management accounts and per bank; pay per payroll and ledger. */
function TriangulationSection() {
  const project = useDiligenceStore((s) => s.project)!;
  const upload = useDiligenceStore((s) => s.uploadDocument);
  const remove = useDiligenceStore((s) => s.removeDocument);
  const currency = project.statements.currency || 'GBP';
  const tab = project.assessment.triangulation;
  return (
    <Section
      title="VAT returns and payroll: triangulation"
      note="VAT returns (CSV, PDF or text) are used where their boxes add up; box 6 is compared with the management accounts' revenue for the quarter (2%) and the bank's receipts with box 6 plus box 1 (10%). Payroll summaries (CSV) are compared with the staff costs a financial year at a time (2%)."
    >
      <div className="flex flex-wrap items-center gap-2">
        <UploadButton label="Add VAT returns" accept=".csv,.pdf,.txt" onFile={(f) => void upload('vat-returns', f)} />
        <UploadButton label="Add a payroll summary" accept=".csv" onFile={(f) => void upload('payroll', f)} />
      </div>
      {tab && (
        <p className="mt-2 text-xs font-medium text-amber-700" data-validation="triangulation">
          {tab.validation}
        </p>
      )}
      <ul className="mt-2 space-y-1 text-xs text-gray-500">
        {(project.vatReturns ?? []).map((r) => (
          <li key={r.name} className="flex items-center gap-2">
            VAT {r.start ?? '?'} to {r.end ?? '?'} · {r.name} ·{' '}
            <span className={r.proved ? 'text-emerald-700' : 'text-red-700'}>{r.proved ? 'adds up' : r.fault}</span>
            <button type="button" className="text-gray-400 underline" onClick={() => void remove('vat-returns', r.name)}>
              remove
            </button>
          </li>
        ))}
        {(project.payeSummaries ?? []).map((p) => (
          <li key={p.name} className="flex items-center gap-2">
            Payroll · {p.name} · {p.months.length} pay periods
            <button type="button" className="text-gray-400 underline" onClick={() => void remove('payroll', p.name)}>
              remove
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-3">
        <Findings findings={tab?.findings ?? []} currency={currency} />
      </div>
    </Section>
  );
}

/** The report's narrative: written by the model from accepted findings, kept by code. */
function NarrativeSection() {
  const project = useDiligenceStore((s) => s.project)!;
  const writeNarrative = useDiligenceStore((s) => s.writeNarrative);
  const isSaving = useDiligenceStore((s) => s.isSaving);
  const narrative = project.narrative;
  return (
    <Section
      title="Narrative"
      note="Written by the model from the findings you have accepted, and nothing else. A paragraph is kept only where every finding it names is accepted and every figure in it is one of theirs; the rest are listed as dropped, with why."
    >
      <Button size="sm" variant="secondary" disabled={isSaving} onClick={() => void writeNarrative()}>
        {narrative ? 'Write again' : 'Write the narrative'}
      </Button>
      {narrative && (
        <div className="mt-3 space-y-2 text-sm text-gray-800">
          {narrative.paragraphs.map((p, i) => (
            <p key={i} data-narrative-findings={p.findings.join(' ')}>
              {p.text} <span className="text-xs text-gray-400">[{p.findings.join(', ')}]</span>
            </p>
          ))}
          {narrative.dropped.length > 0 && (
            <ul className="list-disc pl-5 text-xs text-gray-500">
              {narrative.dropped.map((d) => (
                <li key={d}>Dropped: {d}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Section>
  );
}

function DataRoomTab() {
  const project = useDiligenceStore((s) => s.project)!;
  const uploadManagementAccounts = useDiligenceStore((s) => s.uploadManagementAccounts);
  const removeManagementAccounts = useDiligenceStore((s) => s.removeManagementAccounts);
  const uploadContract = useDiligenceStore((s) => s.uploadContract);
  const decideClause = useDiligenceStore((s) => s.decideClause);
  const currency = project.statements.currency || 'GBP';
  const pack = project.managementAccounts;
  const packFindings = project.assessment.overview.findings.filter((f) => f.id.startsWith('documents:management'));
  const notAssessed = (project.assessment.documents.notAssessed ?? []).filter((n) => !n.startsWith('Going concern'));
  return (
    <div className="space-y-4">
      <Section
        title="Management accounts"
        note="A workbook or CSV with a header row of years and one row per line. Each row is matched to a statement line by name and compared with the filed figure; a difference above 1% is a finding. A row not recognised is listed, never guessed."
      >
        <div className="flex items-center gap-2">
          <UploadButton label={pack ? 'Replace' : 'Upload management accounts'} accept=".xlsx,.xlsm,.csv" onFile={(f) => void uploadManagementAccounts(f)} />
          {pack && (
            <Button size="sm" variant="secondary" onClick={() => void removeManagementAccounts()}>
              Remove
            </Button>
          )}
        </div>
        {pack && (
          <>
            <p className="mt-2 text-xs text-gray-500">
              {pack.name} · uploaded {pack.uploadedAt.slice(0, 10)} · {pack.rows.length} rows,{' '}
              {pack.rows.filter((r) => r.field).length} matched
            </p>
            <div className="mt-3">
              <Findings findings={packFindings} currency={currency} />
            </div>
          </>
        )}
      </Section>
      <MonthlySeriesSection />
      <ProofOfCashSection />
      <TriangulationSection />
      <Section
        title="Contracts"
        note="A contract's text is read for change of control, termination, exclusivity and assignment. The model proposes; a clause is kept only where its quote is on the page it cites; you accept or reject it. The contract itself is not stored."
      >
        <UploadButton label="Read a contract" accept=".pdf,.txt" onFile={(f) => void uploadContract(f)} />
        {project.contracts.length > 0 && (
          <ul className="mt-2 text-xs text-gray-500">
            {project.contracts.map((c, i) => (
              <li key={i}>
                {c.name} · {c.pages} pages · {c.clausesFound} clauses kept, {c.clausesDropped} dropped by the quote check
              </li>
            ))}
          </ul>
        )}
        <ul className="mt-3 divide-y divide-gray-100">
          {project.clauses.map((c) => (
            <li key={c.id} className="py-2 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="font-medium">{c.kind}</span>{' '}
                  <span className="text-xs text-gray-500">
                    {c.document}, page {c.page} · {c.status}
                  </span>
                  <p className="mt-0.5 text-xs italic text-gray-700">&ldquo;{c.quote}&rdquo;</p>
                  {c.summary && <p className="text-xs text-gray-600">{c.summary}</p>}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="secondary" disabled={c.status === 'accepted'} onClick={() => void decideClause(c.id, 'accepted')}>
                    Accept
                  </Button>
                  <Button size="sm" variant="secondary" disabled={c.status === 'rejected'} onClick={() => void decideClause(c.id, 'rejected')}>
                    Reject
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </Section>
      <NotAssessed lines={notAssessed} />
    </div>
  );
}

function Tab({ tab }: { tab: string }) {
  const project = useDiligenceStore((s) => s.project)!;
  const updateConfig = useDiligenceStore((s) => s.updateConfig);
  const decideAdjustment = useDiligenceStore((s) => s.decideAdjustment);
  const proposeAdjustments = useDiligenceStore((s) => s.proposeAdjustments);
  const restore = useDiligenceStore((s) => s.restore);
  const refresh = useDiligenceStore((s) => s.refresh);
  const exportReport = useDiligenceStore((s) => s.exportReport);
  const dropped = useDiligenceStore((s) => s.dropped);
  const isSaving = useDiligenceStore((s) => s.isSaving);
  const a = project.assessment;
  const currency = project.statements.currency || 'GBP';
  const money = (v: number | null | undefined) => (v === null || v === undefined ? '-' : formatMoney(v, currency));
  const findingsOf = (name: string) => a.overview.findings.filter((f) => f.tab === name);

  switch (tab) {
    case 'dd-overview':
      return (
        <div className="space-y-4">
          {a.overview.unvouched.length > 0 && (
            <Notice tone="warning">
              {a.overview.unvouched.length} figure{a.overview.unvouched.length === 1 ? '' : 's'} in these statements could not be
              vouched for by the filing: {a.overview.unvouched.slice(0, 4).join('; ')}. Findings resting on them say so.
            </Notice>
          )}
          <Section title={`Findings (${a.overview.findings.length})`} note="Ranked by severity, then amount. Accept or reject each.">
            <Findings findings={a.overview.findings} currency={currency} />
          </Section>
          <NotAssessed lines={a.overview.notAssessed} />
        </div>
      );
    case 'dd-quality': {
      const q = a['quality-of-earnings'];
      return (
        <div className="space-y-4">
          <Section
            title="Reported to adjusted EBITDA"
            note="Only accepted adjustments reach the adjusted figure. A model's proposal is kept only where its amount is printed on the table row it quotes."
          >
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500"><th>Year</th><th className="text-right">Reported</th><th className="text-right">Accepted adjustments</th><th className="text-right">Adjusted</th><th className="text-right">Pending</th></tr></thead>
              <tbody>
                {q.rows.map((r) => (
                  <tr key={r.year} className="border-t border-gray-100">
                    <td>FY{r.year}</td><td className="text-right tabular-nums">{money(r.reported)}</td>
                    <td className="text-right tabular-nums">{money(r.accepted)}</td><td className="text-right font-semibold tabular-nums">{money(r.adjusted)}</td>
                    <td className="text-right">{r.pending}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ul className="mt-4 divide-y divide-gray-100">
              {project.adjustments.map((adj) => (
                <li key={adj.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="min-w-0">
                    <span className="font-medium">{adj.label}</span>{' '}
                    <span className="text-xs text-gray-500">FY{adj.year} · {adj.category} · {adj.source} · {adj.proposedBy === 'model' ? 'proposed by the model' : 'entered by you'}</span>
                    {adj.quote && <span className="block text-xs italic text-gray-500">"{adj.quote}"</span>}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="tabular-nums">{money(adj.amount)}</span>
                    {(['accepted', 'rejected'] as Status[]).map((status) => (
                      <button key={status} type="button" aria-pressed={adj.status === status}
                        onClick={() => void decideAdjustment(adj.id, adj.status === status ? 'proposed' : status)}
                        className={clsx('rounded px-2 py-0.5 text-xs ring-1 ring-inset', adj.status === status ? 'bg-gray-900 text-white ring-gray-900' : 'text-gray-600 ring-gray-200')}>
                        {status === 'accepted' ? 'Accept' : 'Reject'}
                      </button>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
            <AdjustmentForm years={project.statements.historicalYears} />
            {project.source.kind === 'linked' && (
              <div className="mt-3">
                <Button size="sm" variant="secondary" disabled={isSaving} onClick={() => void proposeAdjustments()}>
                  Propose adjustments from the notes
                </Button>
                {dropped.length > 0 && (
                  <p className="mt-2 text-xs text-gray-500">Dropped by the check: {dropped.join('; ')}</p>
                )}
              </div>
            )}
          </Section>
          <NotAssessed lines={q.notAssessed} />
        </div>
      );
    }
    case 'dd-net-debt': {
      const n = a['net-debt'];
      return (
        <div className="space-y-4">
          <Section title={`Net debt at FY${n.year ?? '-'}`} note="Each item from the balance sheet; switch the debt-like ones in or out - the switch and its reason are kept with the version.">
            <table className="w-full text-sm">
              <tbody>
                {n.items.map((item) => (
                  <tr key={item.key} className="border-t border-gray-100">
                    <td className="py-1.5">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={item.included}
                          onChange={(e) => void updateConfig({ netDebtOverrides: { [item.key]: e.target.checked } }, `${item.label} ${e.target.checked ? 'in' : 'out'}`)} />
                        {item.label}
                      </label>
                      <span className="block pl-6 text-xs text-gray-500">{item.reason}</span>
                    </td>
                    <td className="text-right tabular-nums">{money(item.amount)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-300 font-semibold"><td className="py-1.5">Net debt</td><td className="text-right tabular-nums">{money(n.total)}</td></tr>
              </tbody>
            </table>
          </Section>
          <Section title="Findings"><Findings findings={findingsOf('net-debt')} currency={currency} /></Section>
        </div>
      );
    }
    case 'dd-working-capital': {
      const w = a['working-capital'];
      return (
        <div className="space-y-4">
          <Section title="Days, by year" note={`Normalised level: the average of the last ${w.normalisedYears} year-end positions, ${money(w.normalised)}.`}>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500"><th>Year</th><th className="text-right">Debtor days</th><th className="text-right">Creditor days</th><th className="text-right">Stock days</th><th className="text-right">Working capital</th></tr></thead>
              <tbody>
                {w.rows.map((r) => (
                  <tr key={r.year} className="border-t border-gray-100">
                    <td>FY{r.year}</td><td className="text-right">{days(r.debtorDays)}</td><td className="text-right">{days(r.creditorDays)}</td>
                    <td className="text-right">{days(r.stockDays)}</td><td className="text-right tabular-nums">{money(r.workingCapital)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <label className="mt-3 flex items-center gap-2 text-xs text-gray-600">
              Years to average
              <select value={project.config.normalisationYears} onChange={(e) => void updateConfig({ normalisationYears: Number(e.target.value) })}
                className="rounded border border-gray-300 px-1 py-0.5">
                {[1, 2, 3, 4, 5].map((n) => <option key={n}>{n}</option>)}
              </select>
            </label>
          </Section>
          <Section title="Findings"><Findings findings={findingsOf('working-capital')} currency={currency} /></Section>
          <NotAssessed lines={w.notAssessed} />
        </div>
      );
    }
    case 'dd-cash': {
      const c = a['cash-conversion'];
      return (
        <div className="space-y-4">
          <Section title="Operating cash against EBITDA and profit; capex against depreciation">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500"><th>Year</th><th className="text-right">Operating cash</th><th className="text-right">EBITDA</th><th className="text-right">Conversion</th><th className="text-right">Capex</th><th className="text-right">Depreciation</th><th className="text-right">Capex / dep.</th></tr></thead>
              <tbody>
                {c.rows.map((r) => (
                  <tr key={r.year} className="border-t border-gray-100">
                    <td>FY{r.year}</td><td className="text-right tabular-nums">{money(r.operatingCashFlow)}</td><td className="text-right tabular-nums">{money(r.ebitda)}</td>
                    <td className="text-right">{pct(r.conversion)}</td><td className="text-right tabular-nums">{money(r.capex)}</td>
                    <td className="text-right tabular-nums">{money(r.depreciation)}</td><td className="text-right">{r.capexToDepreciation === null ? '-' : `${r.capexToDepreciation.toFixed(1)}x`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
          <Section title="Findings"><Findings findings={findingsOf('cash-conversion')} currency={currency} /></Section>
          <NotAssessed lines={c.notAssessed} />
        </div>
      );
    }
    case 'dd-red-flags': {
      const r = a['red-flags'];
      return (
        <div className="space-y-4">
          <Section title="Screens" note="Each with its arithmetic. A screen that cannot be computed names the missing input below.">
            <ul className="divide-y divide-gray-100">
              {r.screens.map((s) => (
                <li key={s.key} className="py-2 text-sm">
                  <span className={clsx('font-medium', s.flagged ? 'text-red-700' : 'text-gray-900')}>{s.label}{s.flagged ? ' - flagged' : ''}</span>
                  <span className="block text-xs text-gray-600">{s.detail}</span>
                </li>
              ))}
            </ul>
          </Section>
          <NotAssessed lines={r.notAssessed} />
        </div>
      );
    }
    case 'dd-corporate':
      return <CorporateTab />;
    case 'dd-requests':
      return <RequestsTab />;
    case 'dd-signals':
      return <SignalsTab />;
    case 'dd-related-parties':
      return <RelatedPartiesTab />;
    case 'dd-data-room':
      return <DataRoomTab />;
    case 'dd-report':
      return (
        <Section title="Red-flag report" note="Headed 'Preliminary findings from the documents provided - not a due diligence opinion', with every finding's source and what was and was not examined.">
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => void exportReport('docx')}>Download Word report</Button>
            <Button variant="secondary" onClick={() => void exportReport('xlsx')}>Download workbook</Button>
          </div>
          <SendToValuation />
          <div className="mt-4">
            <NarrativeSection />
          </div>
        </Section>
      );
    case 'dd-versions':
      return (
        <Section title="Versions" note="Every decision, adjustment and setting is kept; restoring one keeps the history.">
          <ul className="divide-y divide-gray-100">
            {[...project.versions].reverse().map((v) => (
              <li key={v.version} className="flex items-center justify-between py-2 text-sm">
                <span><span className="font-medium">Version {v.version}</span> <span className="text-xs text-gray-500">{v.savedAt.slice(0, 16).replace('T', ' ')} · {v.note}</span></span>
                {v.version !== project.version && (
                  <Button size="sm" onClick={() => void restore(v.version)}>Restore</Button>
                )}
              </li>
            ))}
          </ul>
        </Section>
      );
    default:
      return (
        <Section title="Source">
          {project.source.kind === 'linked' ? (
            <div className="space-y-2 text-sm">
              <p>Pinned from <strong>{project.source.modelName}</strong> on {project.source.pinnedAt?.slice(0, 10)} - years {project.statements.historicalYears.join(', ')}.</p>
              {project.source.modelId ? (
                <Button size="sm" variant="secondary" disabled={isSaving} onClick={() => void refresh()}>Refresh from the accounting project</Button>
              ) : (
                <p className="text-xs text-gray-500">Unlinked: the statements it was pinned with are kept.</p>
              )}
            </div>
          ) : project.source.kind === 'example' ? (
            <p className="text-sm text-gray-600">
              A sample: a fictitious company, its figures, register, management pack and contract invented to
              show what the checks find. Everything you decide here is yours to change.
            </p>
          ) : (
            <p className="text-sm text-gray-600">No statements are linked: only the corporate checks apply.</p>
          )}
        </Section>
      );
  }
}

export function DiligenceWorkspace({
  discipline,
  onSwitchDiscipline,
}: {
  discipline: Discipline;
  onSwitchDiscipline: (id: DisciplineId) => void;
}) {
  const project = useDiligenceStore((s) => s.project);
  const close = useDiligenceStore((s) => s.close);
  const error = useDiligenceStore((s) => s.error);
  const clearError = useDiligenceStore((s) => s.clearError);
  const [active, setActive] = useState(discipline.defaultTab);
  if (!project) return null;
  return (
    <WorkspaceShell
      discipline={discipline}
      onBack={close}
      title={project.name}
      subtitle={
        project.source.kind === 'linked'
          ? `Statements pinned from ${project.source.modelName}`
          : project.source.kind === 'example'
            ? 'Sample - fictitious company and figures'
            : 'No statements linked'
      }
      switcher={<DisciplineSwitch current={discipline} onSwitch={onSwitchDiscipline} />}
      groups={DILIGENCE_GROUPS}
      active={active}
      onSelect={setActive}
      banner={error ? <div className="px-6"><Notice onDismiss={clearError}>{error}</Notice></div> : undefined}
    >
      <div className="mx-auto max-w-5xl p-6">
        <Tab tab={active} />
      </div>
    </WorkspaceShell>
  );
}
